import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/api/authService';
import './OTPVerification.css';

const OTPVerification = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [authData, setAuthData] = useState(null);
  const [resendTimer, setResendTimer] = useState(30);

  useEffect(() => {
    const storedAuthData = sessionStorage.getItem('authData');
    if (!storedAuthData) {
      navigate('/login');
      return;
    }
    setAuthData(JSON.parse(storedAuthData));
  }, [navigate]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerify = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    const result = await authService.verifyOtp(
      authData.phoneNumber,
      authData.email,
      otpString
    );

    if (result.success) {
      setSuccess(true);
      
      sessionStorage.removeItem('authData');
      
      setTimeout(() => {
        navigate('/?showNameModal=true');
      }, 1500);
    } else {
      setError(result.error || 'Verification failed. Please try again.');
    }

    setLoading(false);
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    setError('');

    const result = await authService.requestOtp(
      authData.phoneNumber,
      authData.rastId,
      authData.email,
      'EMAIL'
    );

    if (result.success) {
      setResendTimer(30);
      setOtp(['', '', '', '', '', '']);
    } else {
      setError('Failed to resend OTP. Please try again.');
    }

    setLoading(false);
  };

  if (!authData) {
    return null;
  }

  return (
    <div className="otp-container">
      <div className="otp-card">
        <div className="otp-header">
          <h1 className="otp-title">Verify OTP</h1>
          <p className="otp-subtitle">
            Enter the 6-digit code sent to {authData.email || authData.phoneNumber}
          </p>
        </div>

        {success ? (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <p>Verification successful!</p>
            <p className="redirect-text">Setting up your dining experience...</p>
          </div>
        ) : (
          <>
            <div className="otp-input-container">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="otp-input"
                  disabled={loading}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              ))}
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <button 
              onClick={handleVerify}
              className="verify-button"
              disabled={loading || otp.join('').length !== 6}
            >
              {loading ? (
                <span className="loading-text">
                  Verifying
                  <span className="loading-dots">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </span>
                </span>
              ) : (
                'Verify OTP'
              )}
            </button>

            <div className="resend-section">
              {resendTimer > 0 ? (
                <p className="resend-timer">
                  Resend OTP in {resendTimer}s
                </p>
              ) : (
                <button 
                  onClick={handleResendOTP}
                  className="resend-button"
                  disabled={loading}
                >
                  Resend OTP
                </button>
              )}
            </div>

            <button 
              onClick={() => navigate('/login')}
              className="back-button"
              disabled={loading}
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OTPVerification;
